import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { TenantService } from "@/services/tenantService";
import type { Tenant, PlatformStats } from "@/types/tenant";
import {
  TbBuildingSkyscraper,
  TbBuildingCommunity,
  TbUsers,
  TbChecklist,
} from "react-icons/tb";

export const SuperAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [tenantsData, statsData] = await Promise.all([
        TenantService.listAllTenants(),
        TenantService.getPlatformStats(),
      ]);
      setTenants(tenantsData);
      setStats(statsData);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-4 border-[#374795] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const recentTenants = tenants.slice(0, 5);

  return (
    <div className="w-full space-y-6">
      <h2 className="text-lg font-semibold text-[#444]">Overview</h2>

      {/* Stat Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={TbBuildingSkyscraper}
            label="Total Tenants"
            value={stats.total_tenants}
            borderColor="#374795"
            iconBg="bg-blue-50"
            iconColor="text-[#374795]"
          />
          <StatCard
            icon={TbBuildingCommunity}
            label="Active Tenants"
            value={stats.active_tenants}
            borderColor="#10b981"
            iconBg="bg-emerald-50"
            iconColor="text-emerald-600"
          />
          <StatCard
            icon={TbUsers}
            label="Total Users"
            value={stats.total_users}
            borderColor="#8b5cf6"
            iconBg="bg-purple-50"
            iconColor="text-purple-600"
          />
          <StatCard
            icon={TbChecklist}
            label="Total Tasks"
            value={stats.total_tasks}
            borderColor="#f59e0b"
            iconBg="bg-amber-50"
            iconColor="text-amber-600"
          />
        </div>
      )}

      {/* Recent Tenants */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-[#444]">Recent Tenants</h3>
          <button
            onClick={() => navigate("/platform/tenants")}
            className="text-sm font-medium text-[#374795] hover:text-[#1c398e] transition-colors cursor-pointer"
          >
            View all
          </button>
        </div>
        {recentTenants.length === 0 ? (
          <div className="text-center py-8 text-[#828282] text-sm">
            No tenants yet.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {recentTenants.map((tenant) => {
              const settings = Array.isArray(tenant.tenant_settings)
                ? tenant.tenant_settings[0]
                : tenant.tenant_settings;
              const memberCount = Array.isArray(tenant.tenant_members)
                ? tenant.tenant_members[0]?.count || 0
                : 0;

              return (
                <li
                  key={tenant.id}
                  className="flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => navigate(`/platform/tenants/${tenant.id}`)}
                >
                  <div className="flex items-center gap-3">
                    {settings?.logo_url ? (
                      <img
                        src={settings.logo_url}
                        alt={tenant.name}
                        className="w-8 h-8 rounded-lg object-contain bg-gray-50"
                      />
                    ) : (
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                        style={{
                          backgroundColor: settings?.primary_color || "#374795",
                        }}
                      >
                        {tenant.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-[#444]">
                        {tenant.name}
                      </p>
                      <p className="text-xs text-[#828282]">
                        {tenant.slug}.taskrsol.com
                        {memberCount > 0 && ` · ${memberCount} members`}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`text-xs font-medium px-2 py-1 rounded-full ${
                      tenant.is_active
                        ? "bg-green-50 text-green-700"
                        : "bg-red-50 text-red-600"
                    }`}
                  >
                    {tenant.is_active ? "Active" : "Inactive"}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

const StatCard: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  borderColor: string;
  iconBg: string;
  iconColor: string;
}> = ({ icon: Icon, label, value, borderColor, iconBg, iconColor }) => (
  <div
    className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 flex items-center gap-4"
    style={{ borderLeftWidth: 4, borderLeftColor: borderColor }}
  >
    <div
      className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${iconBg}`}
    >
      <Icon className={`w-6 h-6 ${iconColor}`} />
    </div>
    <div>
      <p className="text-2xl font-bold text-[#444]">{value}</p>
      <p className="text-sm text-[#828282]">{label}</p>
    </div>
  </div>
);
